import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  alpha,
  Stack,
  Grid,
  Divider,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  TablePagination,
} from "@mui/material";
import {
  Science as ScienceIcon,
  FilterList as FilterListIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import axios from "axios";
import PageTitle from "../../../components/PageTitle";
import { decryptText, decryptAppId } from "../../../utils/encryption";
import s3Utils from "../../../utils/s3Utils";

// --------------------------------------------------------------------
// WorkingSpaceDocument – handles images, PDFs, Excel files
// --------------------------------------------------------------------
const WorkingSpaceDocument = ({
  doc,
  index,
  isPdf,
  isExcel,
  colors,
  onImageClick,
}) => {
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDocument = async () => {
      try {
        setLoading(true);
        if (doc) {
          if (s3Utils.isJustFilename(doc)) {
            const signedUrl = await s3Utils.generateSignedUrlForFilename(doc);
            setImageUrl(signedUrl);
          } else {
            setImageUrl(doc);
          }
        }
      } catch (error) {
        console.error("Error loading document:", error);
        setImageUrl("");
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
  }, [doc]);

  if (loading) {
    return (
      <Box
        sx={{
          width: 80,
          height: 80,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: colors.grey[100],
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (isPdf || isExcel) {
    return (
      <Box
        sx={{
          width: 80,
          height: 80,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.2s",
          bgcolor: isPdf
            ? alpha(colors.error.main, 0.05)
            : alpha(colors.success.main, 0.05),
          "&:hover": {
            borderColor: isPdf ? colors.error.main : colors.success.main,
            transform: "scale(1.05)",
            boxShadow: 3,
          },
        }}
        onClick={() => window.open(imageUrl, "_blank")}
      >
        {isPdf ? (
          <PictureAsPdfIcon
            sx={{ fontSize: 30, color: colors.error.main, mb: 1 }}
          />
        ) : (
          <DescriptionIcon
            sx={{ fontSize: 30, color: colors.success.main, mb: 1 }}
          />
        )}
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, textAlign: "center", px: 1 }}
        >
          {isPdf ? "PDF" : "Excel"}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.40rem" }}
        >
          Click to view
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 80,
        height: 80,
        border: `2px solid ${colors.grey[300]}`,
        borderRadius: 2,
        overflow: "hidden",
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
          borderColor: colors.primary.main,
          transform: "scale(1.05)",
          boxShadow: 3,
        },
      }}
      onClick={() =>
        onImageClick ? onImageClick(imageUrl) : window.open(imageUrl, "_blank")
      }
    >
      <img
        src={imageUrl}
        alt={`Evidence ${index + 1}`}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
        onError={(e) => {
          e.target.src =
            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect fill='%23f0f0f0' width='120' height='120'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='12'%3EImage%3C/text%3E%3C/svg%3E";
        }}
      />
    </Box>
  );
};

// --------------------------------------------------------------------
// Main Component – Table View with Expandable Rows
// --------------------------------------------------------------------
export default function TestWitnessReportPage() {
  const location = useLocation();

  const getAssessmentIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encrypted = searchParams.get("assessmentId");
    if (encrypted) {
      try {
        return decryptText(encrypted);
      } catch {
        return encrypted;
      }
    }
    return null;
  };
  const getAppIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encrypted = searchParams.get("appId");
    if (encrypted) {
      try {
        return decryptAppId(encrypted);
      } catch {
        return encrypted;
      }
    }
    return null;
  };

  const [assessmentId] = useState(getAssessmentIdFromQuery());
  const [appId] = useState(getAppIdFromQuery());
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [filterConclusion, setFilterConclusion] = useState("all");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleOpenLightbox = (imageUrl) => {
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage("");
  };

  const colors = {
    primary: { main: "#1976d2" },
    error: { main: "#d32f2f" },
    success: { main: "#2e7d32" },
    grey: { 100: "#f5f5f5", 300: "#e0e0e0" },
  };

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!assessmentId || !appId) {
        setLoading(false);
        showSnackbar("Missing assessmentId or appId", "error");
        return;
      }
      try {
        const url = `${process.env.REACT_APP_API_URL}/assessment-report/test-winess-report?assessmentId=${assessmentId}&appId=${appId}`;
        const response = await axios.get(url);
        if (response.data?.success && Array.isArray(response.data.data)) {
          const formatted = response.data.data.map((item) => {
            let evidence = item.evidence || [];
            if (typeof evidence === "string") {
              try {
                evidence = JSON.parse(evidence);
              } catch {
                evidence = [];
              }
            }
            if (!Array.isArray(evidence)) evidence = [];
            evidence = evidence.map((e) => ({
              Id: e?.Id,
              imgFile: e?.imgFile || "",
              DocId: e?.DocId,
              SourceId: e?.SourceId,
            }));
            return {
              ...item,
              evidence,
            };
          });
          setRecords(formatted);
          setFilteredRecords(formatted);
        } else {
          setRecords([]);
          setFilteredRecords([]);
          showSnackbar("No witness records found", "warning");
        }
      } catch (error) {
        console.error("Failed to fetch witness data:", error);
        showSnackbar("Error loading data", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId, appId]);

  useEffect(() => {
    if (filterConclusion === "all") {
      setFilteredRecords(records);
    } else {
      setFilteredRecords(
        records.filter((r) => r.conclusion?.toLowerCase() === filterConclusion)
      );
    }
    setPage(0); // Reset pagination when filter changes
  }, [filterConclusion, records]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };
  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });
  const handleFilterChange = (e) => setFilterConclusion(e.target.value);

  const formatDiscipline = (discipline) => {
    if (!discipline) return "N/A";
    if (Array.isArray(discipline)) {
      const unique = [...new Set(discipline.filter(Boolean))];
      return unique.join(", ") || "N/A";
    }
    return discipline;
  };

  // Toggle expand
  const handleExpandRow = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Statistics
  const totalCompetent = records.filter(
    (r) => r.conclusion?.toLowerCase() === "competent"
  ).length;
  const totalIncompetent = records.filter(
    (r) => r.conclusion?.toLowerCase() === "incompetent"
  ).length;

  // Paginated data
  const paginatedRecords = filteredRecords.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <PageTitle title="NABL || Test Witness Report" />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box
        sx={{
          p: { xs: 1, md: 2 },
          minHeight: "100vh",
          background: "linear-gradient(145deg, #f0f4f8 0%, #e6edf5 100%)",
        }}
      >
        {/* Header Card */}
        <Card
          sx={{
            mb: 2,
            background: "linear-gradient(135deg, #1a237e 0%, #0d47a1 50%, #1565c0 100%)",
            boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ p: { xs: 1, md: 2 } }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={2}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <ScienceIcon sx={{ fontSize: 30, color: "white" }} />
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: "white",
                      letterSpacing: 1,
                      textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                  >
                    Test Witness Report
                  </Typography>
                </Box>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`Competent: ${totalCompetent}`}
                  sx={{
                    bgcolor: "rgba(76, 175, 80, 0.2)",
                    color: "#a5d6a7",
                    fontWeight: 600,
                    "& .MuiChip-icon": { color: "#81c784" },
                  }}
                />
                <Chip
                  icon={<CancelIcon />}
                  label={`Incompetent: ${totalIncompetent}`}
                  sx={{
                    bgcolor: "rgba(244, 67, 54, 0.2)",
                    color: "#ef9a9a",
                    fontWeight: 600,
                    "& .MuiChip-icon": { color: "#e57373" },
                  }}
                />
                <Chip
                  label={`Total: ${records.length}`}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.15)",
                    color: "white",
                    fontWeight: 600,
                  }}
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Filter Bar */}
        <Paper
          elevation={2}
          sx={{
            p: 2,
            mb: 4,
            borderRadius: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            background: "rgba(255,255,255,0.8)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255,255,255,0.3)",
          }}
        >
          <FilterListIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            Filter by Conclusion:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Conclusion</InputLabel>
            <Select
              value={filterConclusion}
              label="Conclusion"
              onChange={handleFilterChange}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="competent">Competent</MenuItem>
              <MenuItem value="incompetent">Incompetent</MenuItem>
            </Select>
          </FormControl>
          {filterConclusion !== "all" && (
            <Chip
              label={`${filteredRecords.length} results`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Paper>

        {/* Table View */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={10}>
            <CircularProgress size={60} thickness={4} />
          </Box>
        ) : filteredRecords.length === 0 ? (
          <Card sx={{ borderRadius: 4, p: 4, textAlign: "center" }}>
            <ScienceIcon sx={{ fontSize: 80, color: "#ccc", mb: 2 }} />
            <Typography variant="h5" fontWeight={600} color="text.secondary">
              No Witness Records
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {records.length === 0
                ? "No test witness data available for this assessment."
                : "No records match the selected filter."}
            </Typography>
          </Card>
        ) : (
          <Paper sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: "#f5f7fa" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.9rem" }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.9rem" }}>Assessor</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.9rem" }}>Discipline</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.9rem" }}>Test Code</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.9rem" }}>Materials</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.9rem" }}>Test Performed</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.9rem" }}>Method</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.9rem" }}>Conclusion</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.9rem" }} align="center">Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRecords.map((record, index) => {
                    const isCompetent = record.conclusion?.toLowerCase() === "competent";
                    const isIncompetent = record.conclusion?.toLowerCase() === "incompetent";
                    const rowIndex = page * rowsPerPage + index;
                    const isExpanded = expandedRow === rowIndex;

                    return (
                      <React.Fragment key={rowIndex}>
                        <TableRow
                          hover
                          sx={{
                            "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                            borderLeft: `4px solid ${
                              isCompetent ? "#2e7d32" : isIncompetent ? "#d32f2f" : "#ff9800"
                            }`,
                          }}
                        >
                          <TableCell>{rowIndex + 1}</TableCell>
                          <TableCell>{record.AssessorName || "N/A"}</TableCell>
                          <TableCell>{formatDiscipline(record.disciplineName)}</TableCell>
                          <TableCell>{record.TestCode || "N/A"}</TableCell>
                          <TableCell>{record.GroupTested || "N/A"}</TableCell>
                          <TableCell>{record.TypeOfTest || "N/A"}</TableCell>
                          <TableCell>{record.ScopeStandard || "N/A"}</TableCell>
                          <TableCell>
                            <Chip
                              label={record.conclusion || ""}
                              size="small"
                              color={
                                isCompetent
                                  ? "success"
                                  : isIncompetent
                                  ? "error"
                                  : "default"
                              }
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                           <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleExpandRow(rowIndex)}
                            >
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                        </TableRow>

                        {/* Expandable Details Row */}
                        <TableRow>
                          <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 3, bgcolor: "#fafbfc" }}>
                                <Grid container spacing={2}>
                                  {/* Main Details Grid */}
                                  <Grid item xs={12}>
                                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                      Test Details
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6} md={3}>
                                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                                      Materials
                                    </Typography>
                                    <Typography variant="body2">{record.GroupTested || "N/A"}</Typography>
                                  </Grid>
                                  <Grid item xs={6} md={3}>
                                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                                      Test Performed
                                    </Typography>
                                    <Typography variant="body2">{record.TypeOfTest || "N/A"}</Typography>
                                  </Grid>
                                  <Grid item xs={6} md={3}>
                                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                                      Method
                                    </Typography>
                                    <Typography variant="body2">{record.ScopeStandard || "N/A"}</Typography>
                                  </Grid>
                                  <Grid item xs={6} md={3}>
                                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                                      Sample ID
                                    </Typography>
                                    <Typography variant="body2">{record.sampleId || "N/A"}</Typography>
                                  </Grid>
                                  <Grid item xs={6} md={3}>
                                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                                      Test Conducted By
                                    </Typography>
                                    <Typography variant="body2">{record.testConductedBy || "N/A"}</Typography>
                                  </Grid>

                                  {/* Range Info */}
                                  {record.rangeType && (
                                    <Grid item xs={12}>
                                      <Box sx={{ mt: 1, p: 1, bgcolor: "rgba(25,118,210,0.04)", borderRadius: 1 }}>
                                        <Typography variant="caption" fontWeight={600}>
                                          Range Type: {record.rangeType}
                                        </Typography>
                                        <Stack direction="row" spacing={2} flexWrap="wrap">
                                          {record.RangeLL && (
                                            <Typography variant="caption">
                                              <strong>LL:</strong> {record.RangeLL} {record.RangeLLUnit || ""}
                                            </Typography>
                                          )}
                                          {record.RangeUL && (
                                            <Typography variant="caption">
                                              <strong>UL:</strong> {record.RangeUL} {record.RangeULUnit || ""}
                                            </Typography>
                                          )}
                                          {record.rangeRemark && (
                                            <Typography variant="caption">
                                              <strong>Remark:</strong> {record.rangeRemark}
                                            </Typography>
                                          )}
                                        </Stack>
                                      </Box>
                                    </Grid>
                                  )}

                                  {/* Witness-specific details */}
                                  {record.Type_witness === "Re-Testing" && (
                                    <Grid item xs={12}>
                                      <Box sx={{ p: 1.5, bgcolor: "rgba(255,152,0,0.08)", borderRadius: 2, borderLeft: "4px solid #ff9800" }}>
                                        <Typography variant="caption" fontWeight={700} color="warning.dark">
                                          Re‑Testing Details
                                        </Typography>
                                        <Grid container spacing={1} mt={0.5}>
                                          <Grid item xs={6} md={3}>
                                            <Typography variant="caption" component="span" fontWeight={600}>
                                              Earlier Test Date:
                                            </Typography>{" "}
                                            {record.earlierTestdate || "N/A"}
                                          </Grid>
                                          <Grid item xs={6} md={3}>
                                            <Typography variant="caption" component="span" fontWeight={600}>
                                              Earlier Tested By:
                                            </Typography>{" "}
                                            {record.earlierTestedBy || "N/A"}
                                          </Grid>
                                          <Grid item xs={6} md={3}>
                                            <Typography variant="caption" component="span" fontWeight={600}>
                                              Earlier Results:
                                            </Typography>{" "}
                                            {record.earlierReportedResults || "N/A"}
                                          </Grid>
                                          <Grid item xs={6} md={3}>
                                            <Typography variant="caption" component="span" fontWeight={600}>
                                              Witnessed Result:
                                            </Typography>{" "}
                                            {record.resultOfTestWitnessed1 || "N/A"}
                                          </Grid>
                                          <Grid item xs={6} md={3}>
                                            <Typography variant="caption" component="span" fontWeight={600}>
                                              Same Person:
                                            </Typography>{" "}
                                            {record.resultOfTestWitnessedSamePerson || "N/A"}
                                          </Grid>
                                          {record.resultOfTestWitnessedSamePerson === "no" && (
                                            <Grid item xs={6} md={3}>
                                              <Typography variant="caption" component="span" fontWeight={600}>
                                                Other Person:
                                              </Typography>{" "}
                                              {record.testConductedByDifferentPerson || "N/A"}
                                            </Grid>
                                          )}
                                        </Grid>
                                      </Box>
                                    </Grid>
                                  )}

                                  {record.Type_witness === "Testing" && (
                                    <Grid item xs={12}>
                                      <Box sx={{ p: 1.5, bgcolor: "rgba(33,150,243,0.08)", borderRadius: 2, borderLeft: "4px solid #2196f3" }}>
                                        <Typography variant="caption" fontWeight={700} color="info.dark">
                                          Testing Details
                                        </Typography>
                                        <Grid container spacing={1} mt={0.5}>
                                          <Grid item xs={6} md={3}>
                                            <Typography variant="caption" component="span" fontWeight={600}>
                                              Testing of RM:
                                            </Typography>{" "}
                                            {record.testingOfRM || "N/A"}
                                          </Grid>
                                          <Grid item xs={6} md={3}>
                                            <Typography variant="caption" component="span" fontWeight={600}>
                                              Reference Material:
                                            </Typography>{" "}
                                            {record.refernceMaterial || "N/A"}
                                          </Grid>
                                          <Grid item xs={6} md={3}>
                                            <Typography variant="caption" component="span" fontWeight={600}>
                                              Specified Value:
                                            </Typography>{" "}
                                            {record.specifiedValue || "N/A"}
                                          </Grid>
                                          <Grid item xs={6} md={3}>
                                            <Typography variant="caption" component="span" fontWeight={600}>
                                              Witnessed Result:
                                            </Typography>{" "}
                                            {record.resultOfTestWitnessed2 || "N/A"}
                                          </Grid>
                                        </Grid>
                                      </Box>
                                    </Grid>
                                  )}

                                  {/* Deviations & Observations */}
                                  <Grid item xs={6}>
                                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                                      Deviations
                                    </Typography>
                                    <Typography variant="body2" sx={{ p: 1, bgcolor: "#f5f5f5", borderRadius: 1, mt: 0.5 }}>
                                      {record.deviationObserved || "N/A"}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                                      Observation / Remark
                                    </Typography>
                                    <Typography variant="body2" sx={{ p: 1, bgcolor: "#f5f5f5", borderRadius: 1, mt: 0.5 }}>
                                      {record.observation || "N/A"}
                                    </Typography>
                                  </Grid>

                                  {/* CV, MU, PT */}
                                  <Grid item xs={12}>
                                    <Stack direction="row" spacing={2} flexWrap="wrap">
                                      {record.cv && (
                                        <Typography variant="caption">
                                          <strong>CV:</strong> {record.cv}
                                        </Typography>
                                      )}
                                      {record.mu && (
                                        <Typography variant="caption">
                                          <strong>MU:</strong> {record.mu}
                                        </Typography>
                                      )}
                                      {record.PTAvailable && (
                                        <Typography variant="caption">
                                          <strong>PT Available:</strong> {record.PTAvailable}
                                        </Typography>
                                      )}
                                    </Stack>
                                  </Grid>

                                  {/* Evidence */}
                                  {record.evidence && record.evidence.length > 0 && (
                                    <Grid item xs={12}>
                                      <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 1 }}>
                                        📎 Evidence ({record.evidence.length} file{record.evidence.length > 1 ? "s" : ""})
                                      </Typography>
                                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                                        {record.evidence.map((file, idx) => {
                                          const docLower = file.imgFile.toLowerCase();
                                          const isPdf = docLower.endsWith(".pdf");
                                          const isExcel = docLower.match(/\.(xlsx|xls)$/);
                                          const isImage = docLower.match(/\.(jpg|jpeg|png|gif|webp)$/);

                                          return (
                                            <WorkingSpaceDocument
                                              key={idx}
                                              doc={file.imgFile}
                                              index={idx}
                                              isPdf={isPdf}
                                              isExcel={isExcel}
                                              colors={colors}
                                              onImageClick={isImage ? handleOpenLightbox : null}
                                            />
                                          );
                                        })}
                                      </Box>
                                    </Grid>
                                  )}
                                </Grid>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredRecords.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        )}

        {/* Lightbox */}
        {lightboxOpen && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: "rgba(0, 0, 0, 0.92)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
              cursor: "pointer",
            }}
            onClick={handleCloseLightbox}
          >
            <Box sx={{ maxWidth: "90%", maxHeight: "90%" }}>
              <img
                src={lightboxImage}
                alt="Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "90vh",
                  objectFit: "contain",
                  borderRadius: 8,
                }}
              />
            </Box>
            <Button
              variant="contained"
              sx={{
                position: "absolute",
                top: 20,
                right: 20,
                bgcolor: "rgba(255,255,255,0.9)",
                color: "black",
                "&:hover": { bgcolor: "white" },
                borderRadius: 2,
                px: 3,
                fontWeight: 600,
              }}
              onClick={handleCloseLightbox}
            >
              Close
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
}